import { cn } from "@au-schein-de/au-ui-library";
import { useTranslation } from "react-i18next";
import { ProductImage } from "~/components/product-image.component";
import type { VariantItem } from "./variant-selector.component";

function formatDeltaCents(cents: number, locale = "de"): string {
	return new Intl.NumberFormat(locale, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(cents / 100);
}

type VariantCardProps = {
	variant: VariantItem;
	isSelected: boolean;
	priceDeltaCents: number;
	label: string;
	onClick: () => void;
};

export function VariantCard({
	variant,
	isSelected,
	priceDeltaCents,
	label,
	onClick,
}: VariantCardProps) {
	const { t, i18n } = useTranslation("product");
	const isSoldOut = !variant.isAvailable;
	const isDimmed =
		isSoldOut || (variant.isAvailableAtCartVendor === false && !isSelected);

	const formattedDelta = (() => {
		if (isSelected || isSoldOut) return null;
		const sign = priceDeltaCents >= 0 ? "+" : "\u2212";
		return `${sign}\u00A0${formatDeltaCents(Math.abs(priceDeltaCents), i18n.language)}\u00A0\u20AC`;
	})();

	return (
		<button
			type="button"
			onClick={onClick}
			data-testid={`variant-card-${variant.id}`}
			className="flex flex-col gap-2 items-center shrink-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ansay-700 focus-visible:ring-offset-2 rounded-[10.667px]"
		>
			<div
				className={cn(
					"relative size-20 overflow-hidden",
					isSelected
						? "rounded-[9.6px] border-2 border-ansay-700 bg-white"
						: "rounded-[10.667px] border-2 border-transparent bg-gray-200 hover:rounded-[9.87px] hover:border-ansay-700",
				)}
			>
				<div
					className={cn(
						"absolute left-1/2 -translate-x-1/2 size-[54px]",
						isSelected && !isDimmed ? "top-1/2 -translate-y-1/2" : "top-[5px]",
						isDimmed && "grayscale opacity-50",
					)}
				>
					<ProductImage
						src={variant.imageUrl}
						alt={variant.name}
						viewport="thumbnail"
						className="size-full object-contain"
						loading="lazy"
					/>
				</div>

				{formattedDelta && (
					<span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-caption text-gray-700 text-center w-[60px]">
						{formattedDelta}
					</span>
				)}

				{isSoldOut && (
					<span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-caption text-gray-700 text-center w-[60px]">
						{t("variantSoldOut")}
					</span>
				)}
			</div>

			<span
				className={cn(
					"text-body-sm-emphasis whitespace-nowrap",
					isSelected ? "text-ansay-700" : "text-gray-700",
				)}
			>
				{label}
			</span>
		</button>
	);
}
